import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_LABORATORYPERSONNEL_LOAD,
    ACTION_LABORATORYPERSONNEL_LOAD_BY_ID,
    ACTION_LABORATORYPERSONNEL_LOAD_BY_LABORATORY_ID,
    ACTION_LABORATORYPERSONNEL_LOAD_FAILURE,
    ACTION_LABORATORYPERSONNEL_LOAD_SUCCESS,

    ACTION_LABORATORYPERSONNEL_SAVE,
    ACTION_LABORATORYPERSONNEL_SAVE_SUCCESS,
    ACTION_LABORATORYPERSONNEL_SAVE_FAILURE,

    ACTION_LABORATORYPERSONNEL_CREATE,
    ACTION_LABORATORYPERSONNEL_CREATE_SUCCESS,
    ACTION_LABORATORYPERSONNEL_CREATE_FAILURE,

    ACTION_LABORATORYPERSONNEL_VALIDATE,
    ACTION_LABORATORYPERSONNEL_VALIDATE_SUCCESS,
    ACTION_LABORATORYPERSONNEL_VALIDATE_FAILURE,

    ACTION_LABORATORYPERSONNEL_CLEAR,
    ACTION_LABORATORYPERSONNEL_FILTER,

    LaboratoryPersonnelAction
} from '../actions/laboratoryPersonnels';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ILaboratoryPersonnelState {
    formData: Dtos.LaboratoryPersonnel | Dtos.LaboratoryPersonnel[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.LaboratoryPersonnel[];
    formLength: number;
}

const initialState: ILaboratoryPersonnelState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: ILaboratoryPersonnelState, _isLoading: boolean): ILaboratoryPersonnelState {
    const newState: ILaboratoryPersonnelState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: ILaboratoryPersonnelState,
    formData: Dtos.LaboratoryPersonnel | Dtos.LaboratoryPersonnel[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ILaboratoryPersonnelState {
    const newState: ILaboratoryPersonnelState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ILaboratoryPersonnelState, responseStatus: Dtos.ResponseStatus): ILaboratoryPersonnelState {
    const newState: ILaboratoryPersonnelState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ILaboratoryPersonnelState, formFilter: IFormFilter): ILaboratoryPersonnelState {

    const items: Dtos.LaboratoryPersonnel[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.personnel?.displayWithSalutation?.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: ILaboratoryPersonnelState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ILaboratoryPersonnelState, propertiesToClear?: string[]): ILaboratoryPersonnelState {

    if (!propertiesToClear) {
        const newState: ILaboratoryPersonnelState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ILaboratoryPersonnelState, _isSaving: boolean): ILaboratoryPersonnelState {
    const newState: ILaboratoryPersonnelState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ILaboratoryPersonnelState,
    formData: Dtos.LaboratoryPersonnel | Dtos.LaboratoryPersonnel[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ILaboratoryPersonnelState {
    const newState: ILaboratoryPersonnelState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ILaboratoryPersonnelState, responseStatus: Dtos.ResponseStatus): ILaboratoryPersonnelState {
    const newState: ILaboratoryPersonnelState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ILaboratoryPersonnelState, _isCreating: boolean): ILaboratoryPersonnelState {
    const newState: ILaboratoryPersonnelState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ILaboratoryPersonnelState,
    formData: Dtos.LaboratoryPersonnel,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ILaboratoryPersonnelState {
    const newState: ILaboratoryPersonnelState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ILaboratoryPersonnelState, responseStatus: Dtos.ResponseStatus): ILaboratoryPersonnelState {
    const newState: ILaboratoryPersonnelState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: ILaboratoryPersonnelState, _isValidating: boolean): ILaboratoryPersonnelState {
    const newState: ILaboratoryPersonnelState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ILaboratoryPersonnelState,
    formData: Dtos.LaboratoryPersonnel,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ILaboratoryPersonnelState {
    const newState: ILaboratoryPersonnelState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ILaboratoryPersonnelState, responseStatus: Dtos.ResponseStatus): ILaboratoryPersonnelState {
    const newState: ILaboratoryPersonnelState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const LaboratoryPersonnelReducer: Reducer<ILaboratoryPersonnelState> = (state: ILaboratoryPersonnelState = initialState, action: LaboratoryPersonnelAction) => {
    switch (action.type) {
        case ACTION_LABORATORYPERSONNEL_LOAD:
        case ACTION_LABORATORYPERSONNEL_LOAD_BY_ID:
        case ACTION_LABORATORYPERSONNEL_LOAD_BY_LABORATORY_ID:
            return loading(state, true);
        case ACTION_LABORATORYPERSONNEL_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_LABORATORYPERSONNEL_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_LABORATORYPERSONNEL_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_LABORATORYPERSONNEL_SAVE:
            return saving(state, true);
        case ACTION_LABORATORYPERSONNEL_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_LABORATORYPERSONNEL_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_LABORATORYPERSONNEL_CREATE:
            return creating(state, true);
        case ACTION_LABORATORYPERSONNEL_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_LABORATORYPERSONNEL_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_LABORATORYPERSONNEL_VALIDATE:
            return validating(state, true);
        case ACTION_LABORATORYPERSONNEL_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_LABORATORYPERSONNEL_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_LABORATORYPERSONNEL_FILTER:
            return filter(state, action.formFilter);
    }

    return state;
}

export default LaboratoryPersonnelReducer;